import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Input, Button, Table } from "antd";
import type { TableColumnsType } from "antd";
import { useDebounce } from "@react-hook/debounce";
import { ApolloError } from "@apollo/client";
import { HiSearch } from "react-icons/hi";

import { Organizations } from "graphql/generated/graphql";
import { useGetOrganizationsFilteredQuery } from "graphql/queries/get-organizations-filtered.generated";


import routeNames from "constants/route-names";
import { useLoggedUser } from "state/reactive-vars/logged-user-var";
import { useAdminOrganizationsFiltersStore } from "state/zustand-stores/admin-organizations-filters-store";
import { SEARCH_DEBOUNCE_DELAY_MS } from "constants/constants";
import { UserRoleValue, isAuthorityRole, isCustomerRole } from "constants/field-values";
import { hasRoutePermission } from "routes/route-permissions";

const AdminOrganizations: React.FC = () => {
	const { loggedUserValue } = useLoggedUser();

	// An authority manages the organization it belongs to; it never creates others.
	const canAddOrganization = hasRoutePermission(
		routeNames.ADMIN_ORGANIZATION_ADD,
		loggedUserValue.role as UserRoleValue
	);

	const canEditOrganization = useCallback(
		(record: Organizations) => {
			return loggedUserValue.role === UserRoleValue.admin_user || loggedUserValue.organization_id === record.id;
		},
		[loggedUserValue]
	);

	const tableColumns: TableColumnsType = useMemo(
		() => [
			{
				title: "ORGANIZATION",
				dataIndex: "name",
				key: `NAME`
			},
			{
				title: "CONTACT NAME",
				dataIndex: "contact_name",
				key: `CONTACT NAME`
			},
			{
				title: "TYPE",
				dataIndex: "type",
				key: `TYPE`
			},
			{
				title: "USERS",
				dataIndex: "org_users",
				render: (item: any[]) => item.length,
				key: "USERS"
			},
			{
				title: "PROPERTIES",
				dataIndex: "org_properties_aggregate",
				render: item => {
					if (item && item.aggregate && item.aggregate.count) {
						return item.aggregate.count;
					} else {
						return 0;
					}
				},
				key: `PROPERTIES`
			},
			{
				title: "",
				key: "actions",
				render: (field, record) => {
					const path = `${routeNames.ADMIN_ORGANIZATION_EDIT.replace(":organizationId", record.id)}`;

					const handleEditClick = (e: React.MouseEvent) => {
						e.stopPropagation();
					};

					return (
						<div className="basic-row" key={record.id} onClick={e => e.stopPropagation()}>
							{canEditOrganization(record as Organizations) && (
								<Link to={{ pathname: path }} className="inline-link" rel="noopener" onClick={handleEditClick}>
									<img
										alt={`edit-attachment-icon`}
										src={`${process.env.PUBLIC_URL}/assets/images/icons/edit-pencil.svg`}
										className="selected-file-icon"
									/>
								</Link>
							)}
						</div>
					);
				}
			}
		],
		[canEditOrganization]
	);
	const navigate = useNavigate();

	const [searchOrganizationsTerm, setSearchOrganizationsTerm] = useState<string>("");

	const [dataSource, setDataSource] = useState<Array<Organizations>>([]);

	const searchTerm = useAdminOrganizationsFiltersStore(state => state.filters.searchAdminOrganizationsTerm);

	const setSearchTerm = useAdminOrganizationsFiltersStore(state => state.setSearchTerm);

	const [debouncedSearchTerm, setDebouncedSearchTerm] = useDebounce(searchTerm, SEARCH_DEBOUNCE_DELAY_MS);

	useEffect(() => {
		setDebouncedSearchTerm(searchTerm);
	}, [searchTerm]);

	// An admin manages every organization in the workspace; everyone else is scoped to
	// their own, so without one there is nothing for them to list.
	const isWorkspaceAdmin = loggedUserValue.role === UserRoleValue.admin_user;
	const hasOrganizationScope = isWorkspaceAdmin || !!loggedUserValue.organization_id;

	const organizationsFilter = useMemo(() => {
		const whereClause: any = {
			workspace_id: { _eq: loggedUserValue.workspace_id },
			deleted_at: { _is_null: true }
		};

		// Kept apart from the search terms so the two never collapse into the same _or
		const scopeConditions: any[] = [];

		if (isCustomerRole(loggedUserValue.role) || isAuthorityRole(loggedUserValue.role)) {
			// A customer or an authority only manages the organization it belongs to.
			// Authority roles cannot read the vendor relationship used in the vendor filter below.
			scopeConditions.push({ id: { _eq: loggedUserValue.organization_id } });
		} else if (!isWorkspaceAdmin) {
			// A vendor manages its own organization and the customers it serves.
			scopeConditions.push({
				_or: [
					{ id: { _eq: loggedUserValue.organization_id } },
					{
						vendors: {
							vendor_org_id: { _eq: loggedUserValue.organization_id }
						}
					}
				]
			});
		}

		if (debouncedSearchTerm) {
			scopeConditions.push({
				_or: [
					{ name: { _ilike: `%${debouncedSearchTerm}%` } },
					{ contact_name: { _ilike: `%${debouncedSearchTerm}%` } },
					{ email: { _ilike: `%${debouncedSearchTerm}%` } }
				]
			});
		}

		if (scopeConditions.length > 0) {
			whereClause._and = scopeConditions;
		}

		return whereClause;
	}, [debouncedSearchTerm, loggedUserValue, isWorkspaceAdmin]);

	const handleOnClickOrganization = useCallback(record => {
		const { id } = record;
		const path = `${routeNames.ADMIN_ORGANIZATION_DETAILS.replace(":organizationId", id)}`;

		navigate(path);
	}, []);

	const handleOnCompleteOrganizations = useCallback(data => {
		const { organizations } = data;
		setDataSource(organizations);
	}, []);

	const handleOnErrorOrganizations = useCallback((error: ApolloError) => {
		console.log("handleOnErrorOrganizations Error: ", error);
	}, []);

	const { data, loading, error } = useGetOrganizationsFilteredQuery({
		// Hasura drops a null _eq from the where clause, which would list the whole workspace
		skip: !hasOrganizationScope,
		variables: {
			where: organizationsFilter
		},
		errorPolicy: "none",
		fetchPolicy: "network-only",
		nextFetchPolicy: "network-only",
		notifyOnNetworkStatusChange: true,
		onError: handleOnErrorOrganizations,
		onCompleted: handleOnCompleteOrganizations
	});

	const handleSearchTermChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			// Directly update the search term in Zustand
			setSearchTerm(event.target.value);
		},
		[setSearchTerm]
	);

	return (
		<div className="products-screen-container admin-organizations-page">
			<span className="page-title">{"Manage Organizations"}</span>
			<div className="admin-products-content-container">
				<div className="admin-products-filter-group">
					<div className="search-products-row">
						<div className="products-search-container">
							<Input
								className="product-search-input-wrapper"
								name="searchOrganization"
								placeholder="Search..."
								classNames={{ input: "product-search-input" }}
								value={searchTerm} // Use Zustand search term
								autoComplete="off"
								onChange={handleSearchTermChange}
								prefix={<HiSearch color={"#67748E"} size={"16px"} />}
								allowClear
							/>
						</div>
						{canAddOrganization && (
							<Link to={routeNames.ADMIN_ORGANIZATION_ADD} className="highnote-button">
								Add Organization
							</Link>
						)}
					</div>
				</div>

				<div className="admin-products-table">
					<div className="products-table-container">
						<Table
							dataSource={dataSource}
							columns={tableColumns}
							loading={loading}
							rowKey={record => record.id}
							onRow={(record, rowIndex) => {
								return {
									onClick: event => {
										handleOnClickOrganization(record);
									}
								};
							}}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default AdminOrganizations;
